import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

// Status configuration
const APPLICANT_STATUSES = {
  APPLIED: {
    key: 'APPLIED',
    label: 'Applied',
    color: 'bg-blue-100 text-blue-600 border-blue-400',
    icon: '📝',
    description: 'New applications received'
  },
  SHORTLISTED: {
    key: 'SHORTLISTED',
    label: 'Shortlisted',
    color: 'bg-purple-100 text-purple-600 border-purple-400',
    icon: '⭐',
    description: 'Candidates selected for review'
  },
  INTERVIEW_SCHEDULED: {
    key: 'INTERVIEW_SCHEDULED',
    label: 'Interview Scheduled',
    color: 'bg-yellow-100 text-yellow-600 border-yellow-400',
    icon: '📅',
    description: 'Interviews scheduled with candidates'
  },
  OFFER_SENT: {
    key: 'OFFER_SENT',
    label: 'Offer Sent',
    color: 'bg-green-100 text-green-600 border-green-400',
    icon: '✉️',
    description: 'Job offers sent to candidates'
  },
  HIRED: {
    key: 'HIRED',
    label: 'Hired',
    color: 'bg-emerald-100 text-emerald-600 border-emerald-400',
    icon: '🎉',
    description: 'Successfully hired candidates'
  }
};

// Base component for all status pages
const ApplicantStatusPage = ({ status }) => {
  const [applicants, setApplicants] = useState([]);
  const [jobDetails, setJobDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [selectedApplicants, setSelectedApplicants] = useState(new Set());
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { jobId } = useParams();

  const statusConfig = APPLICANT_STATUSES[status];
  const jobsEndpoint = "http://127.0.0.1:8000/api/jobs/"; // Your API endpoint

  useEffect(() => {
    const fetchApplicants = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${jobsEndpoint}${jobId}/applicant_list`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        
        // Filter applicants by status and process with file counts
        const filteredApplicants = (response.data.applicants || [])
          .filter(applicant => applicant.application_status === status)
          .map(applicant => ({
            ...applicant,
            cv_count: applicant.cv_files?.length || 0,
            extra_files_count: applicant.extra_files?.length || 0
          }));
        
        setApplicants(filteredApplicants);        
        
        // Set job details from first applicant
        if (response.data.applicants && response.data.applicants.length > 0) {
          setJobDetails({
            title: response.data.applicants[0].job_title,
            company: response.data.applicants[0].company_name
          });
        }
      } catch (err) {
        setError("Failed to load applicants.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchApplicants();
  }, [jobId, status, token]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredApplicants = useMemo(() => {
    let result = [...applicants];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (applicant) =>
          applicant.job_title?.toLowerCase().includes(searchLower) ||
          applicant.company_name?.toLowerCase().includes(searchLower) ||
          applicant.id.toString().includes(searchLower)
      );
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'date_updated') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [applicants, searchTerm, sortConfig]);

  const toggleDropdown = (applicantId) => {
    setDropdownOpen(dropdownOpen === applicantId ? null : applicantId);
  };

  const handleSelectApplicant = (applicantId) => {
    const newSelected = new Set(selectedApplicants);
    if (newSelected.has(applicantId)) {
      newSelected.delete(applicantId);
    } else {
      newSelected.add(applicantId);
    }
    setSelectedApplicants(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedApplicants.size === filteredApplicants.length) {
      setSelectedApplicants(new Set());
    } else {
      setSelectedApplicants(new Set(filteredApplicants.map(a => a.id)));
    }
  };

  // Status change functions
  const moveToNextStatus = async (applicantIds) => {
    const statusOrder = ['APPLIED', 'SHORTLISTED', 'INTERVIEW_SCHEDULED', 'OFFER_SENT', 'HIRED'];
    const currentIndex = statusOrder.indexOf(status);
    if (currentIndex < statusOrder.length - 1) {
      const nextStatus = statusOrder[currentIndex + 1];
      await updateApplicantStatus(applicantIds, nextStatus);
    }
  };

  const moveToPreviousStatus = async (applicantIds) => {
    const statusOrder = ['APPLIED', 'SHORTLISTED', 'INTERVIEW_SCHEDULED', 'OFFER_SENT', 'HIRED'];
    const currentIndex = statusOrder.indexOf(status);
    if (currentIndex > 0) {
      const previousStatus = statusOrder[currentIndex - 1];
      await updateApplicantStatus(applicantIds, previousStatus);
    }
  };

  const updateApplicantStatus = async (applicantIds, newStatus) => {
    try {
      // This would be your API call to update status
      const response = await axios.patch(`${jobsEndpoint}applicants/bulk-status-update/`, {
        applicant_ids: Array.from(applicantIds),
        status: newStatus
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        }
      });
      
      // Refresh data after update
      window.location.reload();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update applicant status');
    }
  };

  // File download functions (same as original)
  const downloadFile = async (url, filename) => {
    try {
      const response = await axios.get(jobsEndpoint.replace('/jobs/', '') + url, {
        responseType: 'blob',
        headers: {
          'Authorization': `Token ${token}`,
        }
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(`Failed to download ${filename}`);
    }
  };

  const renderFileDownloadDropdown = (applicant) => {
    const cvFiles = applicant.cv_files || [];
    const extraFiles = applicant.extra_files || [];
    
    return (
      <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10 max-h-96 overflow-y-auto">
        <div className="py-1">
          {/* CV Files Section */}
          {cvFiles.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">
                CV Files ({cvFiles.length})
              </div>
              {cvFiles.map((file) => (
                <button
                  key={`cv-${file.id}`}
                  onClick={() => downloadFile(file.url, file.name)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 truncate"
                  title={file.name}
                >
                  📄 {file.name}
                  <span className="text-xs text-gray-500 block">
                    {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Size unknown'}
                  </span>
                </button>
              ))}
            </>
          )}
          
          {/* Extra Files Section */}
          {extraFiles.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">
                Extra Files ({extraFiles.length})
              </div>
              {extraFiles.map((file) => (
                <button
                  key={`extra-${file.id}`}
                  onClick={() => downloadFile(file.url, file.name)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 truncate"
                  title={file.name}
                >
                  📎 {file.name}
                  <span className="text-xs text-gray-500 block">
                    {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Size unknown'}
                  </span>
                </button>
              ))}
            </>
          )}
          
          {/* View Details */}
          <button
            onClick={() => navigate(`/applicant/${applicant.id}`)}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            👁️ View Details
          </button>
        </div>
      </div>
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <button 
            onClick={() => navigate(`/jobs/${jobId}/applicants`)}
            className="text-gray-500 hover:text-gray-700"
          >
            ← Back to All Applicants
          </button>
        </div>
        
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{statusConfig.icon}</span>
              <h1 className="text-2xl font-semibold text-gray-900">
                {statusConfig.label} Applicants
              </h1>
            </div>
            <p className="text-sm text-gray-600">
              {jobDetails && `${jobDetails.title} at ${jobDetails.company} • `}
              {statusConfig.description}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search applicants..."
                value={searchTerm}
                onChange={handleSearch}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              />
            </div>
            <button className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Status Navigation */}
      <div className="mb-6">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {Object.values(APPLICANT_STATUSES).map((statusItem) => (
            <button
              key={statusItem.key}
              onClick={() => navigate(`/jobs/${jobId}/applicants/${statusItem.key.toLowerCase()}`)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                statusItem.key === status
                  ? 'bg-[#19bfb7] text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>{statusItem.icon}</span>
              <span className="text-sm font-medium">{statusItem.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedApplicants.size > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">
              {selectedApplicants.size} applicant{selectedApplicants.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              {status !== 'APPLIED' && (
                <button
                  onClick={() => moveToPreviousStatus(selectedApplicants)}
                  className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  ← Move Back
                </button>
              )}
              {status !== 'HIRED' && (
                <button
                  onClick={() => moveToNextStatus(selectedApplicants)}
                  className="px-3 py-1 text-sm bg-[#19bfb7] text-white rounded hover:bg-[#18ada6]"
                >
                  Move Forward →
                </button>
              )}
              <button
                onClick={() => setSelectedApplicants(new Set())}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading/Error */}
      {loading && <p className="text-gray-500">Loading applicants...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* Applicants Table */}
      {!loading && !error && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="pt-3 px-5">
            <h3 className="text-sm font-semibold text-gray-900">{statusConfig.label} Applicants</h3>
          </div>
          <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200">
            <h3 className="text-2xl text-gray-900 font-semibold">
              {filteredApplicants.length}
            </h3>
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <span>Total in this stage</span>
            </div>
          </div>

          <div>
            {/* Table Header */}
            <div className="px-5 py-3 grid md:grid-cols-8 grid-cols-5 border-b border-gray-200">
              <div className="flex gap-3 items-center md:col-span-2 col-span-2">
                <input 
                  type="checkbox" 
                  className="rounded border-gray-300"
                  checked={selectedApplicants.size === filteredApplicants.length && filteredApplicants.length > 0}
                  onChange={handleSelectAll}
                />
                <h5 
                  className="text-gray-500 font-semibold cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('id')}
                >
                  Applicant ID
                  {sortConfig.key === 'id' && (
                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </h5>
              </div>
              <div className="grid md:grid-cols-6 grid-cols-3 md:col-span-6 col-span-3">
                <h5 className="text-gray-500 font-semibold text-xs">Status</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">CV Files</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Extra Files</h5>
                <h5 className="text-gray-500 font-semibold text-xs">AI Score</h5>
                <h5 
                  className="text-gray-500 font-semibold text-xs cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('date_updated')}
                >
                  Updated Date
                  {sortConfig.key === 'date_updated' && (
                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </h5>
                <h5 className="text-gray-500 font-semibold text-xs">Actions</h5>
              </div>
            </div>

            {/* Table Rows */}
            {filteredApplicants.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-4">{statusConfig.icon}</div>
                <p className="text-lg font-medium mb-2">No {statusConfig.label.toLowerCase()} applicants</p>
                <p className="text-sm">There are currently no applicants in the {statusConfig.label.toLowerCase()} stage.</p>
              </div>
            ) : (
              filteredApplicants.map((applicant) => (
                <div key={applicant.id} className="grid md:grid-cols-8 grid-cols-5 items-center p-4 border-b border-gray-200 hover:bg-gray-50">
                  <div className="flex items-center gap-3 md:col-span-2 col-span-2">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300"
                      checked={selectedApplicants.has(applicant.id)}
                      onChange={() => handleSelectApplicant(applicant.id)}
                    />
                    <p className="md:font-medium font-bold text-xs text-gray-900">
                      #{applicant.id}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-6 grid-cols-3 md:col-span-6 col-span-3 items-center">
                    <div>
                      <span className={`text-center text-[12px] border py-1 px-2 rounded-md ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="hidden md:block">
                      <span className="font-medium text-xs text-gray-500">
                        {applicant.cv_count || 0} files
                      </span>
                    </div>
                    <div className="hidden md:block">
                      <span className="font-medium text-xs text-gray-500">
                        {applicant.extra_files_count || 0} files
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-xs text-gray-500">--</p>
                    </div>
                    <div className="flex justify-between items-center w-full md:justify-start">
                      <span className="font-medium text-xs text-gray-500">
                        {new Date(applicant.date_updated).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-end items-center">
                      <button
                        onClick={() => navigate(`/applicant/${applicant.id}`)}
                        className="text-[#19bfb7] hover:text-[#18ada6] text-xs font-medium mr-3"
                      >
                        View
                      </button>
                      <div className="relative">
                        <span
                          className="text-gray-600 cursor-pointer text-xl font-bold"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(applicant.id);
                          }}
                        >
                          &#8942;
                        </span>
                        {dropdownOpen === applicant.id && renderFileDownloadDropdown(applicant)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Individual status page components
export const AppliedPage = () => <ApplicantStatusPage status="APPLIED" />;
export const ShortlistedPage = () => <ApplicantStatusPage status="SHORTLISTED" />;
export const InterviewScheduledPage = () => <ApplicantStatusPage status="INTERVIEW_SCHEDULED" />;
export const OfferSentPage = () => <ApplicantStatusPage status="OFFER_SENT" />;
export const HiredPage = () => <ApplicantStatusPage status="HIRED" />;

// Default export for routing
const ApplicantStatusPages = {
  AppliedPage,
  ShortlistedPage,
  InterviewScheduledPage,
  OfferSentPage,
  HiredPage
};

export default ApplicantStatusPages;